import { Zap, Star, Save, Trash2 } from "lucide-react";
import { US_STATES } from "@/lib/formHelpers";

/** Saved-preset bar shown above the search form. */
export function ApolloSavedPresets({ presets, onApply, onDelete, onSave, saving }) {
  return (
    <div className="mb-6 bg-white border border-neutral-200 p-4 flex items-center gap-3 flex-wrap" data-testid="apollo-saved-searches">
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
        <Star className="w-3 h-3" /> Saved presets
      </div>
      {presets.length === 0 ? (
        <span className="text-xs text-neutral-400">No presets yet — tune your filters, then hit "Save preset".</span>
      ) : (
        presets.map((s) => (
          <div key={s.id} className="inline-flex items-center gap-1 border border-neutral-300 pl-3 pr-1 py-1 text-xs group hover:border-black" data-testid={`apollo-preset-${s.id}`}>
            <button onClick={() => onApply(s)} className="font-medium hover:text-black" data-testid={`apollo-preset-load-${s.id}`}>
              {s.name}
            </button>
            <button
              onClick={() => onDelete(s.id, s.name)}
              className="opacity-40 hover:opacity-100 hover:text-rose-600 ml-1 p-0.5"
              data-testid={`apollo-preset-delete-${s.id}`}
              title="Delete preset"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))
      )}
      <button
        onClick={onSave}
        disabled={saving}
        className="ml-auto lf-btn-outline text-xs inline-flex items-center gap-1.5"
        data-testid="apollo-save-preset-btn"
      >
        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save current filters"}
      </button>
    </div>
  );
}

/** Apollo B2B search form (industry, state, city, employees, per_page, no-website). */
export function ApolloFilters({
  industry, setIndustry,
  stateName, setStateName,
  cityInput, setCityInput,
  employees, setEmployees,
  perPage, setPerPage,
  noWebsite, setNoWebsite,
  onSubmit, busy,
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-neutral-200 p-6 grid grid-cols-1 md:grid-cols-6 gap-4 items-end mb-6"
      data-testid="apollo-search-form"
    >
      <div className="md:col-span-2">
        <label className="overline block mb-2">Industry keywords</label>
        <input
          data-testid="apollo-industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="plumbing, hvac, roofing"
          className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
        />
      </div>
      <div>
        <label className="overline block mb-2">State</label>
        <select
          data-testid="apollo-state"
          value={stateName}
          onChange={(e) => setStateName(e.target.value)}
          className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
        >
          {US_STATES.map(([code, name]) => (
            <option key={code} value={name}>{name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="overline block mb-2">City <span className="text-neutral-400 normal-case tracking-normal">(optional)</span></label>
        <input
          data-testid="apollo-city"
          value={cityInput}
          onChange={(e) => setCityInput(e.target.value)}
          placeholder="Long Beach"
          className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
        />
      </div>
      <div>
        <label className="overline block mb-2">Employees</label>
        <select
          data-testid="apollo-employees"
          value={employees}
          onChange={(e) => setEmployees(e.target.value)}
          className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
        >
          <option value="1,10">1–10 (micro)</option>
          <option value="1,20">1–20</option>
          <option value="11,50">11–50</option>
          <option value="51,200">51–200</option>
          <option value="1,200">1–200 (all SMB)</option>
        </select>
      </div>
      <div>
        <label className="overline block mb-2">Per page</label>
        <select
          data-testid="apollo-perpage"
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
          className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer md:col-span-4">
        <input
          type="checkbox"
          data-testid="apollo-nowebsite"
          checked={noWebsite}
          onChange={(e) => setNoWebsite(e.target.checked)}
          className="w-4 h-4"
        />
        <span>Only show companies <strong>without a website</strong> (your niche)</span>
      </label>
      <button
        data-testid="apollo-search-btn"
        className="lf-btn-primary flex items-center justify-center gap-2 md:col-span-2"
        disabled={busy}
        type="submit"
      >
        <Zap className="w-4 h-4" />
        {busy ? "Searching…" : "Search Apollo"}
      </button>
    </form>
  );
}
